/**
 * Geographic navigation — the start of the site's location hierarchy.
 *
 * The URL shape is the important part of this file, and the expensive thing to
 * change later, so it is decided once here:
 *
 *   /locations                          every state
 *   /locations/texas                    one state
 *   /locations/texas/austin             one city
 *   /locations/texas/austin/roofing     one city + service   (later)
 *
 * Full state names rather than two-letter codes: "texas" matches how people
 * search, and scoping the city under its state is what stops the ~30 US
 * Springfields from colliding. Slugs are public URLs — permanent once minted.
 *
 * SCALE NOTE: this hierarchy can express far more pages than deserve to exist.
 * ~30k US cities against dozens of service categories is millions of
 * combinations, nearly all of them empty or holding a single record. Which
 * combinations actually get generated must be decided from real data density
 * later; nothing here should be read as a mandate to generate all of them.
 */

export const ALL_LOCATIONS_HREF = '/locations'

export const stateHref = (stateSlug: string) => `${ALL_LOCATIONS_HREF}/${stateSlug}`

export const cityHref = (stateSlug: string, citySlug: string) =>
  `${ALL_LOCATIONS_HREF}/${stateSlug}/${citySlug}`

export interface FeaturedLocation {
  city: string
  /** Full state name, for display. */
  state: string
  /** Two-letter code, for compact display. */
  stateCode: string
  citySlug: string
  stateSlug: string
  /** The two cities given the larger treatment in the homepage grid. */
  featured?: boolean
}

/**
 * TEMPORARY discovery examples, not a ranking.
 *
 * These six cities are placeholders chosen to show the layout; they carry no
 * contractor counts because none have been measured. When real data lands this
 * becomes a query — most-covered cities, or cities near the visitor.
 */
export const FEATURED_LOCATIONS: readonly FeaturedLocation[] = [
  {
    city: 'Austin',
    state: 'Texas',
    stateCode: 'TX',
    citySlug: 'austin',
    stateSlug: 'texas',
    featured: true,
  },
  {
    city: 'Los Angeles',
    state: 'California',
    stateCode: 'CA',
    citySlug: 'los-angeles',
    stateSlug: 'california',
    featured: true,
  },
  { city: 'Miami', state: 'Florida', stateCode: 'FL', citySlug: 'miami', stateSlug: 'florida' },
  { city: 'Phoenix', state: 'Arizona', stateCode: 'AZ', citySlug: 'phoenix', stateSlug: 'arizona' },
  { city: 'Denver', state: 'Colorado', stateCode: 'CO', citySlug: 'denver', stateSlug: 'colorado' },
  {
    city: 'Charlotte',
    state: 'North Carolina',
    stateCode: 'NC',
    citySlug: 'charlotte',
    stateSlug: 'north-carolina',
  },
]

export interface PopularState {
  name: string
  slug: string
}

/**
 * The eight states the processed dataset covers most heavily.
 *
 * The ORDER reflects real record volume (California ~75k down to North
 * Carolina ~15.5k), but the counts themselves are deliberately not stored or
 * displayed. They are pre-import figures that no page can currently stand
 * behind, and a number on screen reads as a live count. They belong in the UI
 * only once the data is loaded and can be queried.
 */
export const POPULAR_STATES: readonly PopularState[] = [
  { name: 'California', slug: 'california' },
  { name: 'Texas', slug: 'texas' },
  { name: 'Indiana', slug: 'indiana' },
  { name: 'Florida', slug: 'florida' },
  { name: 'New York', slug: 'new-york' },
  { name: 'Arizona', slug: 'arizona' },
  { name: 'Illinois', slug: 'illinois' },
  { name: 'North Carolina', slug: 'north-carolina' },
]
